import { Text } from "@clipboard-health/ui-react";
import { isDefined } from "@clipboard-health/util-ts";
import { Card, CardContent, CardHeader } from "@mui/material";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { formatInTimeZone } from "date-fns-tz";

import {
  ShiftAction,
  type ShiftBoardShiftLogs,
  type ShiftBoardWorkplace,
  ShiftSubAction,
} from "../types";

// eslint-disable-next-line complexity
function getShiftLogText(log: ShiftBoardShiftLogs): string | null {
  const workerName = log.worker?.name;
  const performedByName = log.performedBy?.name;
  const performedByType = log.performedBy?.type;
  const isClipboardSupport = performedByType !== "FACILITY";

  switch (log.action) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    case ShiftAction.SHIFT_OPEN: {
      if (log.subAction === ShiftSubAction.WORKER_LEFT_EARLY_WITHOUT_PERMISSION) {
        return `This urgent shift was posted because ${
          isClipboardSupport ? "Clipboard Support" : performedByName
        } indicated that ${
          workerName ? `${workerName}` : "the worker"
        } left the shift early without permission`;
      }

      if (log.subAction === ShiftSubAction.WORKER_LEFT_EARLY_WITH_PERMISSION) {
        return `This urgent shift was posted because ${
          isClipboardSupport ? "Clipboard Support" : performedByName
        } indicated that ${
          workerName ? `${workerName}` : "the worker"
        } left the shift early with permission`;
      }

      return `Shift was posted by ${isClipboardSupport ? "Clipboard Support" : performedByName}`;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    case ShiftAction.SHIFT_CLAIM: {
      return `Shift was claimed${workerName ? ` by ${workerName}` : ""}`;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    case ShiftAction.FACILITY_CANCEL_FILLED:
    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison, no-fallthrough
    case ShiftAction.FACILITY_CANCEL: {
      return `Shift was cancelled by ${
        isClipboardSupport ? "Clipboard Support at your workplace's request" : performedByName
      }`;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    case ShiftAction.MARKED_AS_NON_URGENT: {
      return `We've stopped looking for a replacement worker for this shift`;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    case ShiftAction.WORKER_CANCEL: {
      return `${
        workerName ? `${workerName}` : "The worker"
      } cancelled and was unassigned from this shift`;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    case ShiftAction.NO_CALL_NO_SHOW: {
      if (log.subAction === ShiftSubAction.SELF_NO_CALL_NO_SHOW) {
        return `${
          workerName ? `${workerName}` : "The worker"
        } cancelled shift last minute, so an urgent shift was posted`;
      }

      if (log.subAction === ShiftSubAction.REPLACEMENT_REQUESTED) {
        return `${
          workerName ? `${workerName}` : "The worker"
        } was marked as a No Show and a replacement was requested by ${
          isClipboardSupport ? "Clipboard Support" : performedByName
        }, so an urgent shift was posted`;
      }

      if (log.subAction === ShiftSubAction.NO_CALL_NO_SHOW) {
        return `${workerName ? `${workerName}` : "The worker"} was marked as a No Show by ${
          isClipboardSupport ? "Clipboard Support" : performedByName
        }`;
      }

      return null;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    case ShiftAction.SHIFT_UNASSIGN: {
      if (log.subAction === ShiftSubAction.NO_CALL_NO_SHOW) {
        return `Clipboard detected that ${
          workerName ? `${workerName}` : "the worker"
        } was a no show or running too late so they were unassigned from the shift`;
      }

      if (log.subAction === ShiftSubAction.WORKER_LEFT_EARLY_WITHOUT_PERMISSION) {
        return `${
          isClipboardSupport ? "Clipboard Support, at your workplace's request," : performedByName
        } indicated that ${
          workerName ? `${workerName}` : "the worker"
        } left the shift early without permission`;
      }

      if (log.subAction === ShiftSubAction.WORKER_LEFT_EARLY_WITH_PERMISSION) {
        return `${
          isClipboardSupport ? "Clipboard Support, at your workplace's request," : performedByName
        } indicated that ${
          workerName ? `${workerName}` : "the worker"
        } left the shift early with permission`;
      }

      return null;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    case ShiftAction.REPLACEMENT_SHIFT_POSTED: {
      if (
        log.subAction === ShiftSubAction.WORKER_LEFT_EARLY_WITHOUT_PERMISSION ||
        log.subAction === ShiftSubAction.WORKER_LEFT_EARLY_WITH_PERMISSION
      ) {
        return `A replacement urgent shift was posted by ${
          isClipboardSupport ? "Clipboard Support, at your workplace's request" : performedByName
        }`;
      }

      return null;
    }

    default: {
      return null;
    }
  }
}

export function ShiftBoardShiftHistory({
  logs,
  workplace,
}: {
  logs: ShiftBoardShiftLogs[];
  workplace: ShiftBoardWorkplace;
}) {
  return (
    <Card variant="outlined" sx={{ backgroundColor: "#FAFAFA" }}>
      <CardHeader sx={{ paddingBottom: 0 }} title={<Text variant="h3">Shift history</Text>} />
      <CardContent>
        {logs.length > 0 ? (
          logs.map((log) => {
            if (!isDefined(log.eventAt)) {
              return null;
            }

            const text = getShiftLogText(log);
            if (!text) {
              return null;
            }

            return (
              <Text key={log.id} variant="body2" sx={{ marginBottom: "4px" }}>
                {formatInTimeZone(log.eventAt, workplace.timeZone, "M/dd h:mm aa")}
                <span style={{ color: "black" }}>: {text}</span>
              </Text>
            );
          })
        ) : (
          <Text variant="body2">No history available</Text>
        )}
      </CardContent>
    </Card>
  );
}
